import {FormattedMessage, injectIntl} from '@meiko/react-intl';
import PanelHeaderTitle from 'components/generic/PanelHeaderTitle';
import Panel from 'components/generic/Panel';
import React, {useState} from 'react';
import {compose} from 'redux';
import {useStateDebounced} from 'hooks/useStateDebounced';
import Button from 'components/ui/Button';
import Badge from 'components/ui/Badge';
import {Settings} from 'lucide-react';
import clsx from 'clsx';
import {useAuth} from 'hooks/useAuth';
import TargetEditorModal from 'views/TargetEditor/TargetEditorModal';
import {formatCurrency, formatPercentage, formatCount} from 'utils/numberFormat';

const TargetRow = ({title, value, target, formattingStyle}) => {
	// 0-100
	const percent = target === 0 && !!value ? 100 : parseInt((value / target) * 100) || 0;

	const progress = Math.min(
		100,
		target === 0 && !!value ? 100 : parseInt((value / target) * 100) || 0,
	);

	const barColor =
		progress < 15 ? 'bg-red-500' : progress < 70 ? 'bg-yellow-500' : 'bg-teal-500';

	const barClasses = clsx(
		'absolute inset-0 h-1 bg-green-400 rounded-md w-40 transition-all transition-delay-300 ease-in-out duration-500',
		barColor,
	);

	const percentageVariant = progress < 15 ? 'red' : progress < 70 ? 'yellow' : 'teal';

	// enable animations
	const percentDebounced = useStateDebounced(progress, 0, 300);

	return (
		<div className="flex flex-col gap-1">
			<div className="flex justify-between items-center">
				<span className="font-medium">{title}</span>
				<div className="flex justify-end gap-5 items-center">
					<span className="text-zinc-500">
						<span>
							{formattingStyle === 'count' ? formatCount(value) : formatCurrency(value)}
						</span>
						<span className="mx-0.5">/</span>
						<span>
							{formattingStyle === 'count' ? formatCount(target) : formatCurrency(target)}
						</span>
					</span>
					<Badge variant={percentageVariant}>{formatPercentage(percent / 100)}</Badge>
				</div>
			</div>
			<div className="h-1 bg-gray-200 w-full rounded-md relative">
				<div className={barClasses} style={{width: percentDebounced + '%'}} />
			</div>
		</div>
	);
};

const SalesTargets = ({data}) => (
	<div className="flex flex-col gap-4 px-2">
		{data?.map(({label, targets, dealsSum}, i) => (
			<TargetRow
				key={i}
				title={label}
				formattingStyle="currency"
				target={targets['dashboard-sales-sum']}
				value={dealsSum}
			/>
		))}
	</div>
);

const AllTargets = ({data}) => (
	<div className="flex flex-col gap-4">
		{data?.map(({label, targets, dealsSum, successfulVisits, offers}, i) => (
			<div key={i} className="flex flex-col gap-2">
				<span className="block text-center text-sm py-1 border-b border-b-zinc-100">
					{label}
				</span>
				<div className="flex flex-col gap-4 px-2">
					<TargetRow
						title={<FormattedMessage id="Sales" />}
						formattingStyle="currency"
						target={targets['dashboard-sales-sum']}
						value={dealsSum}
					/>
					<TargetRow
						title={<FormattedMessage id="Visits" />}
						formattingStyle="count"
						target={targets['dashboard-visits-count']}
						value={successfulVisits}
					/>
					<TargetRow
						title={<FormattedMessage id="Offers" />}
						formattingStyle="count"
						target={targets['dashboard-offers-count']}
						value={offers}
					/>
				</div>
			</div>
		))}
	</div>
);

const GoalTrackingPanel = ({data}) => {
	const {user} = useAuth();
	const canEditDashboardTargets = !!user?.permissions.find(
		p => p.slug === 'users.dashboard-targets',
	);
	const groupedBy = data?.[0]?.groupedBy;
	const [dashboardTargetsModalOpen, setDashboardTargetsModalOpen] = useState(false);
	const totalDealsSum = data?.reduce((acc, curr) => acc + curr.dealsSum, 0);
	const totalDealsTargetSum = data?.reduce(
		(acc, curr) => acc + curr.targets['dashboard-sales-sum'],
		0,
	);

	return (
		<Panel
			className="!px-0"
			header={
				<div className="flex justify-between items-center">
					<PanelHeaderTitle>
						<FormattedMessage id="Goal tracking" />
					</PanelHeaderTitle>
					<div className="flex items-center gap-2 justify-end">
						<Badge variant="teal" className="gap-0.5" effect="inset">
							{formatCurrency(totalDealsSum)}/{formatCurrency(totalDealsTargetSum)}
						</Badge>
						{canEditDashboardTargets && (
							<Button
								size="sm"
								variant="ghost"
								onClick={() => setDashboardTargetsModalOpen(true)}
							>
								<Settings className="size-4" />
							</Button>
						)}
					</div>
				</div>
			}
		>
			<div className="pt-4 pb-6 max-h-[480px] overflow-y-auto px-2 -mx-2">
				{groupedBy === 'user' ? <AllTargets data={data} /> : <SalesTargets data={data} />}
			</div>
			{canEditDashboardTargets && (
				<TargetEditorModal
					show={dashboardTargetsModalOpen}
					onClose={() => setDashboardTargetsModalOpen(false)}
					initialTab="targets"
				/>
			)}
		</Panel>
	);
};

export default compose(injectIntl)(GoalTrackingPanel);
